import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Star,
  Loader2,
  Award,
  Linkedin,
  Globe,
  X,
  Users,
  UserCheck,
  UserX,
  Phone,
  Mail,
  Video,
  Eye,
  ToggleLeft,
  ToggleRight,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Radio,
  Play,
  CheckCircle,
  FileVideo,
  Presentation,
  Calendar,
  Clock,
  ArrowRight,
  FolderOpen,
} from 'lucide-react';
import { adminAPI, speakersAPI, webinarsAPI, seminarsAPI } from '@/services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatET } from '@/utils/dateUtils';
import MediaPickerModal from '@/components/common/MediaPickerModal';

// Default avatar for inactive speakers
const DEFAULT_AVATAR = 'https://images.unsplash.com/photo-1511367461989-f85a21fda167?w=200&h=200&fit=crop';

const SPEAKERS_PER_PAGE = 20;

export default function AdminSpeakers() {
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // View drawer state
  const [showViewDrawer, setShowViewDrawer] = useState(false);
  const [viewingSpeaker, setViewingSpeaker] = useState(null);
  const [viewTab, setViewTab] = useState('info');
  const [speakerWebinars, setSpeakerWebinars] = useState([]);
  const [speakerSeminars, setSpeakerSeminars] = useState([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [webinarFilter, setWebinarFilter] = useState('all');
  const [seminarFilter, setSeminarFilter] = useState('all');
  const [speakerMediaPickerOpen, setSpeakerMediaPickerOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    headline: '',
    bio: '',
    expertise_areas: [],
    certifications: [],
    linkedin_url: '',
    website_url: '',
    years_experience: '',
    is_featured: false,
    is_active: true,
    profile_image: '',
    speaker_code: '',
  });
  const [newExpertise, setNewExpertise] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [speakerCodeError, setSpeakerCodeError] = useState('');

  useEffect(() => {
    fetchSpeakers();
  }, []);

  // Reset page when tab or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  const fetchSpeakers = async () => {
    try {
      setLoading(true);
      const response = await speakersAPI.getAll({ limit: 1000, include_inactive: true });
      setSpeakers(response.data || []);
    } catch (error) {
      console.error('Error fetching speakers:', error);
      toast.error('Failed to load speakers');
    } finally {
      setLoading(false);
    }
  };

  const fetchSpeakerContent = async (speakerId) => {
    try {
      setLoadingContent(true);
      const [webinarsRes, seminarsRes] = await Promise.all([
        webinarsAPI.getAll({ speaker_id: speakerId, per_page: 100 }),
        seminarsAPI.getAll({ speaker_id: speakerId, per_page: 100 }),
      ]);
      
      const webinarsData = webinarsRes.data?.items || webinarsRes.data || [];
      const seminarsData = seminarsRes.data?.items || seminarsRes.data || [];
      
      setSpeakerWebinars(Array.isArray(webinarsData) ? webinarsData : []);
      setSpeakerSeminars(Array.isArray(seminarsData) ? seminarsData : []);
    } catch (error) {
      console.error('Error fetching speaker content:', error);
    } finally {
      setLoadingContent(false);
    }
  };

  const handleViewSpeaker = async (speaker) => {
    setViewingSpeaker(speaker);
    setViewTab('info');
    setWebinarFilter('all');
    setSeminarFilter('all');
    setShowViewDrawer(true);
    await fetchSpeakerContent(speaker.id);
  };

  const handleOpenDialog = (speaker = null) => {
    setSpeakerCodeError('');
    if (speaker) {
      setEditingSpeaker(speaker);
      setFormData({
        first_name: speaker.first_name || '',
        last_name: speaker.last_name || '',
        email: speaker.email || '',
        phone: speaker.phone || '',
        headline: speaker.headline || '',
        bio: speaker.bio || '',
        expertise_areas: speaker.expertise_areas || [],
        certifications: speaker.certifications || [],
        linkedin_url: speaker.linkedin_url || '',
        website_url: speaker.website_url || '',
        years_experience: speaker.years_experience?.toString() || '',
        is_featured: speaker.is_featured || false,
        is_active: speaker.is_active !== false,
        profile_image: speaker.profile_image || '',
        speaker_code: speaker.speaker_code || '',
      });
    } else {
      setEditingSpeaker(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        headline: '',
        bio: '',
        expertise_areas: [],
        certifications: [],
        linkedin_url: '',
        website_url: '',
        years_experience: '',
        is_featured: false,
        is_active: true,
        profile_image: '',
        speaker_code: '',
      });
    }
    setShowDialog(true);
  };

  // Validate speaker code uniqueness
  const validateSpeakerCode = async (code) => {
    if (!code) {
      setSpeakerCodeError('');
      return true;
    }
    try {
      const excludeId = editingSpeaker?.id;
      const url = excludeId 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/speakers/validate-code/${code}?exclude_speaker_id=${excludeId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/speakers/validate-code/${code}`;
      const response = await fetch(url);
      const data = await response.json();
      if (!data.is_valid) {
        setSpeakerCodeError(data.message);
        return false;
      }
      setSpeakerCodeError('');
      return true;
    } catch (error) {
      console.error('Error validating speaker code:', error);
      return true;
    }
  };

  const handleSaveSpeaker = async () => {
    try {
      setSaving(true);
      const data = {
        ...formData,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
      };

      if (editingSpeaker) {
        await adminAPI.updateSpeaker(editingSpeaker.id, data);
        toast.success('Speaker updated successfully');
      } else {
        await adminAPI.createSpeaker(data);
        toast.success('Speaker created successfully');
      }
      setShowDialog(false);
      fetchSpeakers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save speaker');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSpeaker = async (speaker) => {
    if (!window.confirm(`Are you sure you want to delete ${speaker.first_name} ${speaker.last_name}?`)) {
      return;
    }
    try {
      await adminAPI.deleteSpeaker(speaker.id);
      toast.success('Speaker deleted successfully');
      fetchSpeakers();
    } catch (error) {
      toast.error('Failed to delete speaker');
    }
  };

  const handleToggleActive = async (speaker) => {
    try {
      await adminAPI.updateSpeaker(speaker.id, { is_active: !speaker.is_active });
      toast.success(`Speaker ${speaker.is_active ? 'deactivated' : 'activated'}`);
      fetchSpeakers();
    } catch (error) {
      toast.error('Failed to update speaker');
    }
  };

  const handleToggleFeatured = async (speaker) => {
    try {
      await adminAPI.updateSpeaker(speaker.id, { is_featured: !speaker.is_featured });
      toast.success(`Speaker ${speaker.is_featured ? 'unfeatured' : 'featured'}`);
      fetchSpeakers();
    } catch (error) {
      toast.error('Failed to update speaker');
    }
  };

  const addExpertise = () => {
    if (newExpertise.trim() && !formData.expertise_areas.includes(newExpertise.trim())) {
      setFormData({
        ...formData,
        expertise_areas: [...formData.expertise_areas, newExpertise.trim()],
      });
      setNewExpertise('');
    }
  };

  const removeExpertise = (area) => {
    setFormData({
      ...formData,
      expertise_areas: formData.expertise_areas.filter(a => a !== area),
    });
  };

  const addCertification = () => {
    if (newCertification.trim() && !formData.certifications.includes(newCertification.trim())) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, newCertification.trim()],
      });
      setNewCertification('');
    }
  };

  const removeCertification = (cert) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter(c => c !== cert),
    });
  };

  // Filter and paginate speakers
  const filteredSpeakers = speakers.filter(speaker => {
    const isActiveMatch = activeTab === 'active' ? speaker.is_active !== false : speaker.is_active === false;
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${speaker.first_name || ''} ${speaker.last_name || ''}`.toLowerCase();
    const searchMatch = fullName.includes(searchLower) || speaker.email?.toLowerCase().includes(searchLower);
    return isActiveMatch && searchMatch;
  });

  const totalPages = Math.ceil(filteredSpeakers.length / SPEAKERS_PER_PAGE);
  const startIndex = (currentPage - 1) * SPEAKERS_PER_PAGE;
  const paginatedSpeakers = filteredSpeakers.slice(startIndex, startIndex + SPEAKERS_PER_PAGE);

  const activeSpeakersCount = speakers.filter(s => s.is_active !== false).length;
  const inactiveSpeakersCount = speakers.filter(s => s.is_active === false).length;

  // Filter webinars/seminars for view drawer
  const filteredWebinars = speakerWebinars.filter(w => {
    if (webinarFilter === 'all') return true;
    if (webinarFilter === 'live') return w.webinar_type === 'live';
    if (webinarFilter === 'recorded') return w.webinar_type === 'on_demand' || w.webinar_type === 'recorded';
    if (webinarFilter === 'certified') return w.cpe_credits > 0 || w.has_cpe_credits;
    return true;
  });

  const filteredSeminars = speakerSeminars.filter(s => {
    if (seminarFilter === 'all') return true;
    if (seminarFilter === 'live') return s.seminar_type === 'live';
    if (seminarFilter === 'recorded') return s.seminar_type === 'on_demand' || s.seminar_type === 'recorded';
    if (seminarFilter === 'certified') return s.cpe_credits > 0 || s.has_cpe_credits;
    return true;
  });

  // Pagination component
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between px-4 py-3 border-t">
        <div className="text-sm text-slate-500">
          Showing {startIndex + 1}-{Math.min(startIndex + SPEAKERS_PER_PAGE, filteredSpeakers.length)} of {filteredSpeakers.length}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="h-8 w-8"
          >
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="h-8 w-8"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-3 text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="h-8 w-8"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="h-8 w-8"
          >
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'On Demand';
    return formatET(dateStr, 'MMM dd, yyyy');
  };

  return (
    <div className="space-y-6" data-testid="admin-speakers">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy-700">Speaker Management</h2>
          <p className="text-slate-500">Manage expert speakers and their profiles</p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-navy-600 hover:bg-navy-700 rounded-xl"
          data-testid="add-speaker-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Speaker
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-navy-100 rounded-lg">
              <Users className="w-6 h-6 text-navy-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Speakers</p>
              <p className="text-2xl font-bold text-navy-700">{speakers.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-emerald-100 rounded-lg">
              <UserCheck className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Active Speakers</p>
              <p className="text-2xl font-bold text-emerald-600">{activeSpeakersCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-slate-100 rounded-lg">
              <UserX className="w-6 h-6 text-slate-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Inactive Speakers</p>
              <p className="text-2xl font-bold text-slate-600">{inactiveSpeakersCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-4">
          <TabsList className="bg-slate-100 p-1 rounded-lg">
            <TabsTrigger 
              value="active" 
              className="data-[state=active]:bg-white data-[state=active]:text-emerald-600 px-6"
            >
              <UserCheck className="w-4 h-4 mr-2" />
              Active ({activeSpeakersCount})
            </TabsTrigger>
            <TabsTrigger 
              value="inactive" 
              className="data-[state=active]:bg-white data-[state=active]:text-slate-600 px-6"
            >
              <UserX className="w-4 h-4 mr-2" />
              Inactive ({inactiveSpeakersCount})
            </TabsTrigger>
          </TabsList>
          
          {/* Search */}
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search speakers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-lg"
              data-testid="search-speakers"
            />
          </div>
        </div>

        {/* Speakers Table */}
        <Card className="border-slate-200 rounded-xl overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-semibold">Speaker</TableHead>
                        <TableHead className="font-semibold">Code</TableHead>
                        <TableHead className="font-semibold">Contact</TableHead>
                        <TableHead className="font-semibold">Experience</TableHead>
                        <TableHead className="font-semibold">Webinars</TableHead>
                        <TableHead className="font-semibold">Status</TableHead>
                        <TableHead className="font-semibold text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedSpeakers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                            No {activeTab} speakers found
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedSpeakers.map((speaker) => (
                          <TableRow key={speaker.id} className="hover:bg-slate-50">
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <img
                                  src={speaker.is_active !== false 
                                    ? (speaker.profile_image || DEFAULT_AVATAR)
                                    : DEFAULT_AVATAR
                                  }
                                  alt={`${speaker.first_name} ${speaker.last_name}`}
                                  className={`w-12 h-12 rounded-full object-cover ${
                                    speaker.is_active === false ? 'opacity-50 grayscale' : ''
                                  }`}
                                />
                                <div>
                                  <p className="font-medium text-slate-800 flex items-center gap-2">
                                    {speaker.first_name} {speaker.last_name}
                                    {speaker.is_featured && (
                                      <Star className="w-4 h-4 text-gold-500 fill-gold-500" />
                                    )}
                                  </p>
                                  <p className="text-xs text-slate-500 line-clamp-1 max-w-[200px]">
                                    {speaker.headline || speaker.short_bio || '-'}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {speaker.speaker_code ? (
                                <Badge variant="outline" className="font-mono text-xs bg-slate-100">
                                  {speaker.speaker_code}
                                </Badge>
                              ) : (
                                <span className="text-slate-400 text-xs">Not set</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {speaker.email && (
                                  <p className="text-sm text-slate-600 flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    <span className="truncate max-w-[150px]">{speaker.email}</span>
                                  </p>
                                )}
                                {speaker.phone && (
                                  <p className="text-sm text-slate-500 flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {speaker.phone}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-600">
                              <div className="flex items-center gap-1">
                                <Award className="w-4 h-4 text-navy-500" />
                                {speaker.years_experience ? `${speaker.years_experience} yrs` : '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Video className="w-4 h-4 text-slate-400" />
                                <span>{speaker.total_webinars || 0}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={speaker.is_active !== false ? 'default' : 'secondary'}
                                className={speaker.is_active !== false 
                                  ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' 
                                  : 'bg-slate-100 text-slate-600'
                                }
                              >
                                {speaker.is_active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleViewSpeaker(speaker)}
                                  className="h-8 w-8"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4 text-navy-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleToggleActive(speaker)}
                                  className="h-8 w-8"
                                  title={speaker.is_active ? 'Deactivate' : 'Activate'}
                                >
                                  {speaker.is_active !== false ? (
                                    <ToggleRight className="w-5 h-5 text-emerald-500" />
                                  ) : (
                                    <ToggleLeft className="w-5 h-5 text-slate-400" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleToggleFeatured(speaker)}
                                  className={`h-8 w-8 ${speaker.is_featured ? 'text-gold-600' : 'text-slate-400'}`}
                                  title={speaker.is_featured ? 'Remove Featured' : 'Mark Featured'}
                                >
                                  <Star className={`w-4 h-4 ${speaker.is_featured ? 'fill-gold-500' : ''}`} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleOpenDialog(speaker)}
                                  className="h-8 w-8"
                                  title="Edit"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteSpeaker(speaker)}
                                  className="h-8 w-8 text-red-500 hover:text-red-700"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                {renderPagination()}
              </>
            )}
          </CardContent>
        </Card>
      </Tabs>

      {/* View Speaker Slide-out Drawer */}
      <Sheet open={showViewDrawer} onOpenChange={setShowViewDrawer}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-3">
              {viewingSpeaker && (
                <>
                  <img
                    src={viewingSpeaker.profile_image || DEFAULT_AVATAR}
                    alt={`${viewingSpeaker.first_name} ${viewingSpeaker.last_name}`}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <span className="text-navy-700">{viewingSpeaker.first_name} {viewingSpeaker.last_name}</span>
                    <p className="text-sm font-normal text-slate-500">{viewingSpeaker.email}</p>
                  </div>
                </>
              )}
            </SheetTitle>
          </SheetHeader>
          
          {viewingSpeaker && (
            <div className="mt-6">
              <Tabs value={viewTab} onValueChange={setViewTab}>
                <TabsList className="w-full grid grid-cols-3 mb-6">
                  <TabsTrigger value="info">Speaker Info</TabsTrigger>
                  <TabsTrigger value="webinars">
                    Webinars ({speakerWebinars.length})
                  </TabsTrigger>
                  <TabsTrigger value="seminars">
                    Seminars ({speakerSeminars.length})
                  </TabsTrigger>
                </TabsList>

                {/* Speaker Info Tab */}
                <TabsContent value="info" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-500 text-xs">First Name</Label>
                      <p className="font-medium">{viewingSpeaker.first_name}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Last Name</Label>
                      <p className="font-medium">{viewingSpeaker.last_name}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Email</Label>
                      <p className="font-medium">{viewingSpeaker.email || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Phone</Label>
                      <p className="font-medium">{viewingSpeaker.phone || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Experience</Label>
                      <p className="font-medium">{viewingSpeaker.years_experience || 0} years</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Rating</Label>
                      <p className="font-medium flex items-center gap-1">
                        <Star className="w-4 h-4 text-gold-500 fill-gold-500" />
                        {viewingSpeaker.rating?.toFixed(1) || '4.5'}
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-500 text-xs">Bio</Label>
                    <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">
                      {viewingSpeaker.bio || 'No bio available'}
                    </p>
                  </div>
                  
                  {viewingSpeaker.expertise_areas?.length > 0 && (
                    <div>
                      <Label className="text-slate-500 text-xs">Expertise Areas</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {viewingSpeaker.expertise_areas.map((area, idx) => (
                          <Badge key={idx} variant="secondary">{area}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {viewingSpeaker.certifications?.length > 0 && (
                    <div>
                      <Label className="text-slate-500 text-xs">Certifications</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {viewingSpeaker.certifications.map((cert, idx) => (
                          <Badge key={idx} variant="outline">
                            <Award className="w-3 h-3 mr-1" />
                            {cert}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Webinars Tab */}
                <TabsContent value="webinars" className="space-y-4">
                  {/* Filters */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={webinarFilter === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setWebinarFilter('all')}
                      className={webinarFilter === 'all' ? 'bg-navy-600' : ''}
                    >
                      All ({speakerWebinars.length})
                    </Button>
                    <Button
                      variant={webinarFilter === 'live' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setWebinarFilter('live')}
                      className={webinarFilter === 'live' ? 'bg-red-500' : ''}
                    >
                      <Radio className="w-3 h-3 mr-1" />
                      Live
                    </Button>
                    <Button
                      variant={webinarFilter === 'recorded' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setWebinarFilter('recorded')}
                      className={webinarFilter === 'recorded' ? 'bg-blue-500' : ''}
                    >
                      <FileVideo className="w-3 h-3 mr-1" />
                      Recorded
                    </Button>
                    <Button
                      variant={webinarFilter === 'certified' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setWebinarFilter('certified')}
                      className={webinarFilter === 'certified' ? 'bg-emerald-500' : ''}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Certified
                    </Button>
                  </div>
                  
                  {loadingContent ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-navy-500" />
                    </div>
                  ) : filteredWebinars.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Video className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                      No webinars found
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredWebinars.map((webinar) => (
                        <Card key={webinar.id} className="border-slate-200">
                          <CardContent className="p-3 flex items-center gap-3">
                            <img
                              src={webinar.thumbnail_url || 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=100'}
                              alt={webinar.title}
                              className="w-16 h-12 object-cover rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-navy-700 truncate">{webinar.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className={webinar.webinar_type === 'live' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'} variant="secondary">
                                  {webinar.webinar_type === 'live' ? 'Live' : 'Recorded'}
                                </Badge>
                                {webinar.webinar_type === 'live' && webinar.scheduled_date && (
                                  <span className="text-xs text-slate-500">{formatDate(webinar.scheduled_date)}</span>
                                )}
                              </div>
                            </div>
                            <span className="font-bold text-navy-700">${webinar.price || 0}</span>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Seminars Tab */}
                <TabsContent value="seminars" className="space-y-4">
                  {/* Filters */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={seminarFilter === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSeminarFilter('all')}
                      className={seminarFilter === 'all' ? 'bg-navy-600' : ''}
                    >
                      All ({speakerSeminars.length})
                    </Button>
                    <Button
                      variant={seminarFilter === 'live' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSeminarFilter('live')}
                      className={seminarFilter === 'live' ? 'bg-red-500' : ''}
                    >
                      <Radio className="w-3 h-3 mr-1" />
                      Live
                    </Button>
                    <Button
                      variant={seminarFilter === 'recorded' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSeminarFilter('recorded')}
                      className={seminarFilter === 'recorded' ? 'bg-blue-500' : ''}
                    >
                      <FileVideo className="w-3 h-3 mr-1" />
                      Recorded
                    </Button>
                    <Button
                      variant={seminarFilter === 'certified' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSeminarFilter('certified')}
                      className={seminarFilter === 'certified' ? 'bg-emerald-500' : ''}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Certified
                    </Button>
                  </div>
                  
                  {loadingContent ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-navy-500" />
                    </div>
                  ) : filteredSeminars.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Presentation className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                      No seminars found
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredSeminars.map((seminar) => (
                        <Card key={seminar.id} className="border-slate-200">
                          <CardContent className="p-3 flex items-center gap-3">
                            <img
                              src={seminar.thumbnail_url || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=100'}
                              alt={seminar.title}
                              className="w-16 h-12 object-cover rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-navy-700 truncate">{seminar.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className={seminar.seminar_type === 'live' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'} variant="secondary">
                                  {seminar.seminar_type === 'live' ? 'Live' : 'Recorded'}
                                </Badge>
                                {seminar.seminar_type === 'live' && seminar.scheduled_date && (
                                  <span className="text-xs text-slate-500">{formatDate(seminar.scheduled_date)}</span>
                                )}
                              </div>
                            </div>
                            <span className="font-bold text-navy-700">${seminar.price || 0}</span>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Add/Edit Speaker Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingSpeaker ? 'Edit Speaker' : 'Add New Speaker'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Smith"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Speaker Code</Label>
                <Input
                  value={formData.speaker_code}
                  onChange={(e) => {
                    const code = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    setFormData({ ...formData, speaker_code: code });
                  }}
                  onBlur={(e) => validateSpeakerCode(e.target.value)}
                  placeholder="e.g., MAZI"
                  maxLength={10}
                  className={speakerCodeError ? 'border-red-500' : ''}
                />
                {speakerCodeError && (
                  <p className="text-xs text-red-500">{speakerCodeError}</p>
                )}
                <p className="text-xs text-slate-500">Unique code for webinar/seminar codes (e.g., GRC-WEB-2026-MAZI-0001)</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Years Experience</Label>
                <Input
                  type="number"
                  value={formData.years_experience}
                  onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })}
                  placeholder="10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Profile Image URL</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.profile_image}
                  onChange={(e) => setFormData({ ...formData, profile_image: e.target.value })}
                  placeholder="https://... or browse Media Library"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSpeakerMediaPickerOpen(true)}
                  className="shrink-0"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Browse
                </Button>
                {formData.profile_image && (
                  <img 
                    src={formData.profile_image} 
                    alt="Preview" 
                    className="w-10 h-10 rounded-full object-cover"
                    onError={(e) => e.target.src = DEFAULT_AVATAR}
                  />
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Headline</Label>
              <Input
                value={formData.headline}
                onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                placeholder="Brief professional headline"
              />
            </div>

            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Professional bio and background..."
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label>Expertise Areas</Label>
              <div className="flex gap-2">
                <Input
                  value={newExpertise}
                  onChange={(e) => setNewExpertise(e.target.value)}
                  placeholder="Add expertise area"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExpertise())}
                />
                <Button type="button" variant="outline" onClick={addExpertise}>
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.expertise_areas.map((area, idx) => (
                  <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                    {area}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeExpertise(area)} />
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Certifications</Label>
              <div className="flex gap-2">
                <Input
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  placeholder="Add certification"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                />
                <Button type="button" variant="outline" onClick={addCertification}>
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.certifications.map((cert, idx) => (
                  <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                    <Award className="w-3 h-3" />
                    {cert}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeCertification(cert)} />
                  </Badge>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Years of Experience</Label>
                <Input
                  type="number"
                  value={formData.years_experience}
                  onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })}
                  placeholder="15"
                />
              </div>
              <div className="space-y-2 flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm">Featured Speaker</span>
                </label>
              </div>
              <div className="space-y-2 flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm">Active Speaker</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>LinkedIn URL</Label>
                <div className="relative">
                  <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={formData.linkedin_url}
                    onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                    placeholder="https://linkedin.com/in/..."
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Website URL</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                    placeholder="https://..."
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveSpeaker}
                disabled={saving || !formData.first_name}
                className="bg-navy-600 hover:bg-navy-700"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingSpeaker ? 'Save Changes' : 'Create Speaker'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <MediaPickerModal
        open={speakerMediaPickerOpen}
        onClose={() => setSpeakerMediaPickerOpen(false)}
        onSelect={(url) => {
          setFormData({ ...formData, profile_image: url });
          setSpeakerMediaPickerOpen(false);
          toast.success('Speaker image selected from Media Library');
        }}
        folder="speakers"
      />
    </div>
  );
}
